import { z } from 'zod'

import { TimestampSchema } from '@mntn-dev/domain-types'
import type {
  KebabToCamel,
  StripNever,
  ZodInfer,
} from '@mntn-dev/utility-types'

const BooleanSchema = z.boolean()
const StringArraySchema = z.array(z.string())

const EnabledGrandfatherPeriodSchema = z.object({
  enabled: z.literal(true),
  before: TimestampSchema,
})

const DisabledGrandfatherPeriodSchema = z.object({
  enabled: z.literal(false),
})

const GrandfatherPeriodSchema = z.discriminatedUnion('enabled', [
  EnabledGrandfatherPeriodSchema,
  DisabledGrandfatherPeriodSchema,
])

export type GrandfatherPeriod = ZodInfer<typeof GrandfatherPeriodSchema>

export const FlagDefinitions = {
  'account-boundary': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'client',
  },
  'advance-payments': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'allow-manual-bidding-close': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  billing: {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'disable-video-pro-certification': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'download-quickframe-sow': {
    value: BooleanSchema,
    defaultValue: true,
    kind: 'both',
  },
  'flag-maker-for-removal': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'is-project-bidding-disabled': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'is-project-creation-disabled': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'is-project-mutation-disabled': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'magicsky-restricted-email-notifications': {
    value: StringArraySchema,
    defaultValue: [],
    kind: 'server',
  },
  'magicsky-restricted-logins': {
    value: StringArraySchema.optional(),
    defaultValue: [],
    kind: 'client',
  },
  'allow-50-50-billing-schedule': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'maintenance-mode': {
    value: BooleanSchema.nullable(), // false and null mean the same thing, but null doesn't trigger the same UX - which avoids runaway refreshing when turned off.
    defaultValue: false,
    kind: 'both',
  },
  payments: {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'project-capping-bid-scoring-improvements': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'secure-inngest-events': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'server',
  },
  'show-intercom-widget': {
    value: BooleanSchema,
    defaultValue: true,
    kind: 'client',
  },
  'skip-final-minimum-charge': {
    value: GrandfatherPeriodSchema,
    defaultValue: { enabled: false },
    kind: 'server',
  },
  'transfer-credits': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
  'notification-preferences': {
    value: BooleanSchema,
    defaultValue: false,
    kind: 'both',
  },
} as const

export type FlagTag = keyof typeof FlagDefinitions
export type FlagValue<T extends FlagTag> = ZodInfer<
  (typeof FlagDefinitions)[T]['value']
>

export type ServerFlag = {
  [K in keyof typeof FlagDefinitions]: (typeof FlagDefinitions)[K]['kind'] extends
    | 'server'
    | 'both'
    ? K
    : never
}[keyof typeof FlagDefinitions]

export type ServerFlags = StripNever<{
  [K in keyof typeof FlagDefinitions as KebabToCamel<K>]: (typeof FlagDefinitions)[K]['kind'] extends
    | 'server'
    | 'both'
    ? ZodInfer<(typeof FlagDefinitions)[K]['value']>
    : never
}>

export type ClientFlags = StripNever<{
  [K in keyof typeof FlagDefinitions as KebabToCamel<K>]: (typeof FlagDefinitions)[K]['kind'] extends
    | 'client'
    | 'both'
    ? ZodInfer<(typeof FlagDefinitions)[K]['value']>
    : never
}>
